import { useState, useEffect } from 'react';
import { Pose } from '@mediapipe/pose';

const useAnalysis = (file, isInputVideo) => {
  const [landmarks, setLandmarks] = useState(null);
  const [worldLandmarks, setWorldLandmarks] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) {
      setIsLoading(false);
      setError(null);
      setLandmarks(null);
      setWorldLandmarks(null);
      return;
    }

    let isMounted = true;
    let poseInstance = new Pose({
      locateFile: (file) => `/mediapipe/${file}`,
    });
    let videoElement = null;

    const setupPose = async () => {
      setIsLoading(true);
      setError(null);
      setLandmarks(null);
      setWorldLandmarks(null);
      
      try {
        poseInstance.setOptions({
          modelComplexity: 1,
          smoothLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });
        
        poseInstance.onResults(results => {
          if (!isMounted) return;
          
          if (results.poseLandmarks && results.poseWorldLandmarks) {
            setLandmarks(results.poseLandmarks);
            setWorldLandmarks(results.poseWorldLandmarks);
            setIsLoading(false);
          } else {
            setError('No pose landmarks detected in the frame.');
            setIsLoading(false);
          }
        });

        if (isInputVideo) {
          // --- Video Analysis Logic ---
          videoElement = document.createElement('video');
          videoElement.muted = true;
          videoElement.src = URL.createObjectURL(file);
          videoElement.playsInline = true;

          videoElement.onloadeddata = () => {
            videoElement.play().then(() => {
              // Now that video is playing, start the frame loop
              const processFrame = async () => {
                if (isMounted && !videoElement.paused && !videoElement.ended) {
                  try {
                    await poseInstance.send({ image: videoElement });
                    requestAnimationFrame(processFrame);
                  } catch (e) {
                    if (isMounted) setError(`Frame analysis failed: ${e.message}`);
                    setIsLoading(false);
                  }
                } else if (isMounted && (videoElement.paused || videoElement.ended)) {
                  // Analysis complete or stopped
                  setIsLoading(false);
                }
              };
              processFrame();
            }).catch(e => {
              if (isMounted) setError(`Video playback failed: ${e.message}`);
              setIsLoading(false);
            });
          };

          videoElement.onerror = (e) => {
            if (isMounted) {
              setError('Failed to load video for analysis.');
              setIsLoading(false);
            }
          };

        } else {
          // --- Image Analysis Logic ---
          const imgElement = new Image();
          imgElement.src = URL.createObjectURL(file);
          imgElement.onload = async () => {
            if (isMounted && poseInstance) {
              await poseInstance.send({ image: imgElement });
            }
          };
          imgElement.onerror = (e) => {
            if (isMounted) {
              setError('Failed to load image for analysis.');
              setIsLoading(false);
            }
          };
        }
      } catch (err) {
        if (isMounted) {
          setError(`Analysis setup failed: ${err.message}`);
          setIsLoading(false);
        }
      }
    };

    setupPose();

    // Cleanup function
    return () => {
      isMounted = false;
      if (videoElement) {
        videoElement.pause();
        URL.revokeObjectURL(videoElement.src);
        videoElement = null;
      }
      if (poseInstance) {
        poseInstance.close();
        poseInstance = null;
      }
    };
  }, [file, isInputVideo]);

  return { landmarks, worldLandmarks, isLoading, error };
};

export default useAnalysis;
